import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import wsCookies from 'common/tools/ws-cookies';
import { SnackNotification } from 'common/types/snack-notification';
import { Listing, User } from 'graphql/generated';

import { RootState } from './root-reducer';

interface CacheState {
  listings: Listing[];
  listingsCount?: number;
  listingsYearMax: number;
  listingsYearMin: number;
  loginNavigate: boolean;
  makesPopularFilter: boolean;
  searchAccordianState: string[];
  searchError: boolean;
  snackNotifications: SnackNotification[];
  user: User | undefined;
  pwCode: string;
  fetching: boolean;
  viewIds: string[];
}

const initialState: CacheState = {
  listings: [],
  listingsYearMax: 2022,
  listingsYearMin: 1991,
  loginNavigate: false,
  makesPopularFilter: true,
  searchAccordianState: [],
  searchError: false,
  snackNotifications: [],
  user: undefined,
  pwCode: '',
  fetching: false,
  viewIds: [],
};

const cacheSlice = createSlice({
  name: 'cache',
  initialState,
  reducers: {
    listings(state, action: PayloadAction<Listing[]>) {
      state.listings = action.payload;
    },
    listingsCount(state, action: PayloadAction<number | undefined>) {
      state.listingsCount = action.payload;
    },
    listingsYearMax(state, action: PayloadAction<number>) {
      state.listingsYearMax = action.payload;
    },
    listingsYearMin(state, action: PayloadAction<number>) {
      state.listingsYearMin = action.payload;
    },
    loginNavigate(state, action: PayloadAction<boolean>) {
      state.loginNavigate = action.payload;
    },
    makesPopularFilter(state, action: PayloadAction<boolean>) {
      state.makesPopularFilter = action.payload;
    },
    searchAccordianState(state, action: PayloadAction<string[]>) {
      state.searchAccordianState = action.payload;
    },
    searchError(state, action: PayloadAction<boolean>) {
      state.searchError = action.payload;
    },
    addSnacks(state, action: PayloadAction<SnackNotification[]>) {
      state.snackNotifications = action.payload;
    },
    addSnack(state, action: PayloadAction<SnackNotification>) {
      state.snackNotifications = [
        ...state.snackNotifications,
        { ...action.payload, key: action.payload.key || new Date().getTime() + Math.random() },
      ];
    },
    user(state, action: PayloadAction<User | undefined>) {
      state.user = action.payload;
      if (!state.user) wsCookies.remove('userId');
    },
    pwCode(state, action: PayloadAction<string>) {
      state.pwCode = action.payload;
    },
    fetching(state, action: PayloadAction<boolean>) {
      state.fetching = action.payload;
    },
    addView(state, action: PayloadAction<string>) {
      state.viewIds = [...state.viewIds, action.payload];
    },
  },
});

export const cache = cacheSlice.reducer;
export const cacheActions = {
  ...cacheSlice.actions,
};

const rootState = (state: RootState) => state;
const cacheSliceState = createSelector(rootState, state => state.cache);
export const cacheSelectors = {
  listings: createSelector(cacheSliceState, state => state.listings),
  listingsCount: createSelector(cacheSliceState, state => state.listingsCount),
  listingsYearMax: createSelector(cacheSliceState, state => state.listingsYearMax),
  listingsYearMin: createSelector(cacheSliceState, state => state.listingsYearMin),
  loginNavigate: createSelector(cacheSliceState, state => state.loginNavigate),
  makesPopularFilter: createSelector(cacheSliceState, state => state.makesPopularFilter),
  searchAccordianState: createSelector(cacheSliceState, state => state.searchAccordianState),
  searchError: createSelector(cacheSliceState, state => state.searchError),
  snackNotifications: createSelector(cacheSliceState, state => state.snackNotifications),
  user: createSelector(cacheSliceState, state => state.user),
  pwCode: createSelector(cacheSliceState, state => state.pwCode),
  fetching: createSelector(cacheSliceState, state => state.fetching),
  viewIds: createSelector(cacheSliceState, state => state.viewIds),
};
